// Utilitários para manipulação de imagens em base64

/**
 * Converte um arquivo para base64
 * @param {File} file - Arquivo a ser convertido
 * @returns {Promise<string>} Promise que resolve com a string base64
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

/**
 * Valida um arquivo de imagem
 * @param {File} file - Arquivo a ser validado
 * @param {number} maxSizeMB - Tamanho máximo em MB (padrão: 1MB)
 * @returns {Object} Objeto com valido (boolean) e erro (string) se houver
 */
export const validarImagem = (file, maxSizeMB = 1) => {
  // Verificar se é uma imagem
  if (!file.type.startsWith('image/')) {
    return { 
      valido: false, 
      erro: 'Por favor, selecione apenas arquivos de imagem.' 
    };
  }
  
  // Verificar tamanho
  const maxSize = maxSizeMB * 1024 * 1024;
  if (file.size > maxSize) {
    return { 
      valido: false, 
      erro: `A imagem deve ter no máximo ${maxSizeMB}MB.` 
    };
  }
  
  return { valido: true };
};

/**
 * Obtém a URL da imagem priorizando base64 sobre URL
 * @param {Object} produto - Objeto do produto
 * @returns {string|null} URL da imagem ou null se não houver
 */
export const getImagemProduto = (produto) => {
  if (produto?.imagem_base64) {
    return produto.imagem_base64;
  }
  
  if (produto?.imagem_url && produto.imagem_url !== null) {
    // Adicionar cache busting para URLs
    const separator = produto.imagem_url.includes('?') ? '&' : '?';
    const url = `${produto.imagem_url}${separator}t=${Date.now()}`;
    return url;
  }
  
  return null;
};

/**
 * Redimensiona uma imagem para um tamanho máximo antes de converter para base64
 * @param {File} file - Arquivo de imagem
 * @param {number} maxWidth - Largura máxima em pixels
 * @param {number} maxHeight - Altura máxima em pixels
 * @param {number} quality - Qualidade da compressão (0.1 a 1.0)
 * @returns {Promise<File>} Promise que resolve com o arquivo redimensionado
 */
export const redimensionarImagem = (file, maxWidth = 800, maxHeight = 600, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calcular novas dimensões mantendo proporção
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }
      
      // Configurar canvas
      canvas.width = width;
      canvas.height = height;
      
      // Desenhar imagem redimensionada
      ctx.drawImage(img, 0, 0, width, height);
      
      // Converter para blob
      canvas.toBlob((blob) => {
        // Criar novo arquivo
        const resizedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now()
        });
        resolve(resizedFile);
      }, file.type, quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Calcula o tamanho aproximado de uma string base64 em MB
 * @param {string} base64String - String base64
 * @returns {number} Tamanho em MB
 */
export const calcularTamanhoBase64 = (base64String) => {
  if (!base64String) return 0;
  
  // Remover cabeçalho data:image/...;base64,
  const base64Data = base64String.split(',')[1];
  
  // Calcular tamanho em bytes (base64 é ~33% maior que o arquivo original)
  const bytes = (base64Data.length * 3) / 4;
  
  // Converter para MB
  return bytes / (1024 * 1024);
};
